import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePurchase, useUserContracts } from '../../context/PurchaseContext'; // ✨ Импортируем usePurchase
import { usePendingContractData } from '../common/AuthPrompt';
import LoadingSpinner from '../common/LoadingSpinner';
import PaymentModal from '../Payment/PaymentModal';
import '../../styles/Common.css';
import { getDownloadFilename } from '../../utils/downloadFilename';
import '../../styles/DashboardUpdated.css';

// Компонент ContractRow для отображения одной строки договора (onDownload вызывается с contractId, format, contract для формирования имени файла)
const ContractRow = React.memo(({ contract, onEdit, onDownload, onDelete, onPay, templateName, downloadingFormat }) => {
    const { getContractStatus } = useUserContracts();
    const [isDeleting, setIsDeleting] = useState(false);
    const statusInfo = getContractStatus(contract);
    
    const statusTextMap = {
        'purchased': 'Оплачен',
        'expired': 'Истек',
        'draft': 'Черновик',
        'not_purchased': 'Требует оплаты'
    };

    const handleDelete = async () => {
        if (window.confirm(`Вы уверены, что хотите удалить договор №${contract.data?.header?.number || contract.id.slice(0, 4)}?`)) {
            setIsDeleting(true);
            await onDelete(contract.id);
        }
    };

    const isDownloading = downloadingFormat !== null;
    const isDownloadingWord = downloadingFormat === 'word';
    const isDownloadingPdf = downloadingFormat === 'pdf';

    // ✨ Упрощенное отображение: номер и дата
    const userNumber = contract.data?.header?.number;
    const contractDate = contract.data?.header?.date;
    const displayTemplateName = contract.templateId === 'rent_apartment' 
        ? 'Договор найма жилого помещения' 
        : (templateName || 'Договор');

    return (
        <div className={`contract-row ${isDeleting ? 'deleting' : ''}`}>
            <div className="contract-info">
                <div className="contract-name">
                    <span className="contract-type-label">
                        {displayTemplateName}{userNumber ? ` №${userNumber}` : ''}
                    </span>
                </div>
                {contractDate && <div className="contract-meta">от {contractDate}</div>}
            </div>
            <div className={`contract-status status-${statusInfo.status}`}>{statusTextMap[statusInfo.status] || 'Неизвестен'}</div>
            <div className="contract-actions">
                <button onClick={() => onEdit(contract)} className="action-btn-extended action-edit" disabled={isDeleting}>
                    <span className="action-icon">✏️</span>
                    <span className="action-text">Редактировать</span>
                </button>
                {statusInfo.canDownload ? (
                    <>
                        <button onClick={() => onDownload(contract.id, 'word', contract)} className="action-btn-extended action-download" disabled={isDeleting || isDownloading}>
                            {isDownloadingWord && <span className="btn-spinner" aria-hidden />}
                            <span className="action-icon">{!isDownloadingWord && '⬇️'}</span>
                            <span className="action-text">{isDownloadingWord ? 'Скачивание...' : 'Скачать Word'}</span>
                        </button>
                        <button onClick={() => onDownload(contract.id, 'pdf', contract)} className="action-btn-extended action-download-pdf" disabled={isDeleting || isDownloading}>
                            {isDownloadingPdf && <span className="btn-spinner btn-spinner-dark" aria-hidden />}
                            <span className="action-icon">{!isDownloadingPdf && '⬇️'}</span>
                            <span className="action-text">{isDownloadingPdf ? 'Скачивание...' : 'Скачать PDF'}</span>
                        </button>
                    </>
                ) : (
                    <button onClick={() => onPay(contract)} className="action-btn-extended action-download" disabled={isDeleting}>
                        <span className="action-icon">💳</span>
                        <span className="action-text">Оплатить</span>
                    </button>
                )}
                <button onClick={handleDelete} className="action-btn-extended action-delete" disabled={isDeleting}>
                    <span className="action-icon">{isDeleting ? '⏳' : '🗑️'}</span>
                    <span className="action-text">{isDeleting ? 'Удаление' : 'Удалить'}</span>
                </button>
            </div>
        </div>
    );
});


const DashboardUpdated = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { contracts, isLoading, loadUserContracts, createContract, deleteContract, downloadContract } = useUserContracts();
    const { checkTemplatePurchase, getTemplateById } = usePurchase(); // ✨ Получаем функцию проверки покупки и шаблонов
    const { restoreData, clearPendingData } = usePendingContractData();
    const [isInitializing, setIsInitializing] = useState(true);
    const [paymentModal, setPaymentModal] = useState({ isOpen: false, templateId: null, contractId: null });
    const [downloadingState, setDownloadingState] = useState({ contractId: null, format: null });

    // ✨ Полностью переработанный useEffect для новой логики
    useEffect(() => {
        const initializeDashboard = async () => {
            if (!user) {
                setIsInitializing(false);
                return;
            }

            let pendingSnapshot = restoreData();

            // Если есть данные из гостевого режима — сразу убираем из localStorage (иначе при React Strict Mode
            // или повторном срабатывании эffекта createContract вызывается дважды и в ЛК два одинаковых черновика).
            if (pendingSnapshot && pendingSnapshot.templateId) {
                clearPendingData();
                try {
                    // 1. Проверяем, куплен ли этот шаблон
                    const purchaseStatus = await checkTemplatePurchase(pendingSnapshot.templateId);

                    // 2. Если НЕ куплен - создаем черновик и открываем оплату
                    if (!purchaseStatus.hasPurchase) {
                        try {
                            console.log("Шаблон не оплачен. Создаем черновик и открываем оплату...");
                            const newContract = await createContract(pendingSnapshot.templateId, pendingSnapshot.data);
                            if (newContract && newContract.id) {
                                setPaymentModal({
                                    isOpen: true,
                                    templateId: newContract.templateId,
                                    contractId: newContract.id
                                });
                            } else {
                                throw new Error("Не удалось получить ID созданного договора.");
                            }
                        } catch (error) {
                            try {
                                localStorage.setItem('pending_contract_data', JSON.stringify(pendingSnapshot));
                            } catch {
                                /* ignore */
                            }
                            alert("Произошла ошибка при сохранении вашего черновика. Попробуйте снова.");
                        }
                    } else {
                        // 3. Если куплен - создаем договор
                        try {
                            console.log("Шаблон уже оплачен. Создаем договор...");
                            await createContract(pendingSnapshot.templateId, pendingSnapshot.data);
                        } catch (error) {
                            try {
                                localStorage.setItem('pending_contract_data', JSON.stringify(pendingSnapshot));
                            } catch {
                                /* ignore */
                            }
                            alert("Произошла ошибка при сохранении вашего договора. Попробуйте снова.");
                        }
                    }
                } catch (error) {
                    try {
                        localStorage.setItem('pending_contract_data', JSON.stringify(pendingSnapshot));
                    } catch {
                        /* ignore */
                    }
                }
                pendingSnapshot = null;
            }

            // 4. В любом случае загружаем актуальный список договоров
            await loadUserContracts();
            setIsInitializing(false);
        };

        initializeDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // ✨ Новый обработчик для успешной оплаты
    const handlePaymentSuccess = () => {
        setPaymentModal({ isOpen: false, templateId: null, contractId: null });
        clearPendingData(); // Очищаем данные гостя, так как сценарий завершен
        loadUserContracts(); // Обновляем список, чтобы статус договора изменился на "Оплачен"
        alert('Оплата прошла успешно! Ваш договор сохранен в личном кабинете.');
    };

    const handleEdit = useCallback((contract) => {
        navigate(`/contract/rent?contractId=${contract.id}`);
    }, [navigate]);

    const handleDownload = useCallback(async (contractId, format = 'word', contract = null) => {
        try {
            setDownloadingState({ contractId, format });
            const suggestedFilename = contract ? getDownloadFilename(contract, format, getTemplateById) : null;
            await downloadContract(contractId, format, suggestedFilename);
        } catch (error) {
            alert(`Ошибка скачивания: ${error.message}`);
        } finally {
            setDownloadingState({ contractId: null, format: null });
        }
    }, [downloadContract, getTemplateById]);

    const handleDelete = useCallback(async (contractId) => {
        try {
            await deleteContract(contractId);
        } catch (error) {
            alert(`Ошибка удаления: ${error.message}`);
        }
    }, [deleteContract]);

    const handlePay = useCallback((contract) => {
        setPaymentModal({ isOpen: true, templateId: contract.templateId, contractId: contract.id });
    }, []);

    // Только инициализация ЛК (покупка/черновик из гостя и т.д.). Не скрывать страницу при открытой оплате —
    // иначе PaymentModal ниже не монтируется и пользователь вечно видит «Синхронизация данных...».
    if (isInitializing) {
        return <LoadingSpinner message="Синхронизация данных..." fullScreen />;
    }

    return (
        <>
            <main className="page-section">
                <div className="section-header">
                    <h1>Личный кабинет</h1>
                    <p>Здесь хранятся все ваши созданные договоры. Вы можете их редактировать, скачивать или удалять.</p>
                </div>
                <div className="dashboard-container">
                    {/* ... остальной JSX без изменений ... */}
                    {contracts && contracts.length > 0 ? (
                        <>
                            <div className="dashboard-header">
                                <h3>Ваши договоры ({contracts.length})</h3>
                                <button onClick={() => navigate('/templates')} className="nav-button nav-button-primary">+ Создать новый</button>
                            </div>
                            <div className="contracts-list">
                                {contracts.map(contract => (
                                    <ContractRow
                                        key={contract.id}
                                        contract={contract}
                                        templateName={getTemplateById(contract.templateId)?.name}
                                        onEdit={handleEdit}
                                        onDownload={handleDownload}
                                        onDelete={handleDelete}
                                        onPay={handlePay}
                                        downloadingFormat={downloadingState.contractId === contract.id ? downloadingState.format : null}
                                    />
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-state-icon">📂</div>
                            <h2>У вас пока нет договоров</h2>
                            <p>Начните с выбора подходящего шаблона в нашем каталоге.</p>
                            <button onClick={() => navigate('/templates')} className="nav-button nav-button-primary">Перейти к шаблонам</button>
                        </div>
                    )}
                </div>
            </main>
            <PaymentModal
                isOpen={paymentModal.isOpen}
                templateId={paymentModal.templateId}
                contractId={paymentModal.contractId}
                onClose={() => setPaymentModal({ isOpen: false, templateId: null, contractId: null })}
                onSuccess={handlePaymentSuccess} // ✨ Используем новый обработчик
            />
        </>
    );
};

export default DashboardUpdated;